(() => {
  if (!customElements.get("facets-button")) {
    class FacetsButton extends HTMLElement {
      connectedCallback() {
        this.addEventListener("click", this.openFacets.bind(this));
      }

      openFacets() {
        const facetsWrapper = document.querySelector("custom-facets");
        if (!facetsWrapper) return;

        const panel = facetsWrapper.querySelector("[data-facets-panel]");
        const backdrop = facetsWrapper.querySelector("[data-facets-backdrop]");

        if (panel && backdrop) {
          panel.classList.add("is-open");
          backdrop.classList.add("is-visible");
          document.body.style.overflow = "hidden";
        }
      }
    }

    customElements.define("facets-button", FacetsButton);
  }

  if (!customElements.get("custom-facets")) {
    class CustomFacets extends HTMLElement {
      static SELECTORS = {
        FILTER_INPUTS: ".facets__size-input, .facets__color-input, .facets__option-input",
        FILTER_OPTIONS: ".facets__size-option, .facets__color-option, .facets__option",
        COUNT_ELEMENTS: ".facets__size-count, .facets__option-count",
      };

      connectedCallback() {
        requestAnimationFrame(() => {
          this.init();
        });
      }

      init() {
        this.cacheElements();
        this.initializeState();
        this.setupEventListeners();
        this.setActiveFiltersFromUrl();
      }

      cacheElements() {
        this.panel = this.querySelector("[data-facets-panel]");
        this.backdrop = this.querySelector("[data-facets-backdrop]");
        this.form = this.querySelector("[data-facets-form]");
        this.closeButton = this.querySelector("[data-facets-close]");
        this.clearButton = this.querySelector("[data-facets-clear]");
        this.resultsCount = this.querySelector("[data-facets-results]");
        this.loadingOverlay = this.querySelector(".facets__loading-overlay");
      }

      initializeState() {
        this.availabilityCache = new Map();
        this.isUpdating = false;
        this.pendingUpdate = null;
        this.DEBOUNCE_DELAY = 300;
        this.MAX_CACHE_SIZE = 20;
      }

      setupEventListeners() {
        if (this.closeButton) {
          this.closeButton.addEventListener("click", () => this.close());
        }

        if (this.backdrop) {
          this.backdrop.addEventListener("click", () => this.close());
        }

        if (this.form) {
          this.form.addEventListener("submit", (e) => this.handleSubmit(e));

          this.form.addEventListener("change", (e) => {
            if (e.target.matches('input[type="checkbox"], select')) {
              this.handleFilterChange(e);
            }
          });
        }

        if (this.clearButton) {
          this.clearButton.addEventListener("click", () => this.clear());
        }

        this.handleKeydown = this.handleKeydown.bind(this);
        document.addEventListener("keydown", this.handleKeydown);
      }

      disconnectedCallback() {
        document.removeEventListener("keydown", this.handleKeydown);
      }

      close() {
        if (this.panel && this.backdrop) {
          this.panel.classList.remove("is-open");
          this.backdrop.classList.remove("is-visible");
          document.body.style.overflow = "";
        }
      }

      handleSubmit(event) {
        event.preventDefault();
        this.apply();
      }

      apply() {
        if (!this.form) return;

        const newUrl = this.buildUrlFromForm();
        this.close();
        this.renderPage(newUrl);
      }

      buildUrlFromForm() {
        const formData = new FormData(this.form);
        const params = this.buildSearchParams(formData);
        const url = new URL(window.location);
        return `${url.pathname}?${params.toString()}`;
      }

      buildSearchParams(formData = null) {
        const url = new URL(window.location);
        const params = new URLSearchParams();

        ["q", "type"].forEach((key) => {
          const value = url.searchParams.get(key);
          if (value) params.set(key, value);
        });

        if (formData) {
          for (const [key, value] of formData.entries()) {
            if (value) params.append(key, value);
          }
        }

        return params;
      }

      async renderPage(url) {
        const productGrid = document.querySelector("main-product-grid");
        if (!productGrid) return;

        productGrid.classList.add("is-loading");

        try {
          const html = await this.fetchPageHtml(url);
          const newProductGrid = this.extractProductGrid(html);

          if (newProductGrid) {
            productGrid.innerHTML = newProductGrid.innerHTML;
            window.history.pushState({ path: url }, "", url);

            window.dispatchEvent(
              new CustomEvent("product-grid:updated", {
                detail: { url, productGrid },
              })
            );
          }
        } catch (error) {
          console.error("Error fetching products:", error);
          window.location.href = url;
        } finally {
          productGrid.classList.remove("is-loading");
        }
      }

      async fetchPageHtml(url) {
        const response = await fetch(`${url}&section_id=${this.getSectionId()}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        return response.text();
      }

      extractProductGrid(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        return doc.querySelector("main-product-grid");
      }

      getSectionId() {
        const productGrid = document.querySelector("[data-id]");
        return productGrid ? productGrid.dataset.id : "";
      }

      clear() {
        if (!this.form) return;

        this.resetFormInputs();

        const url = new URL(window.location);
        const params = this.buildSearchParams();
        const newUrl = `${url.pathname}?${params.toString()}`;

        this.close();
        this.renderPage(newUrl);
      }

      resetFormInputs() {
        this.form.querySelectorAll("input, select").forEach((input) => {
          if (input.type === "checkbox" || input.type === "radio") {
            input.checked = false;
          } else {
            input.value = "";
          }
        });
      }

      handleKeydown(event) {
        if (event.key === "Escape" && this.panel?.classList.contains("is-open")) {
          this.close();
        }
      }

      setActiveFiltersFromUrl() {
        if (!this.form) return;

        const params = new URL(window.location).searchParams;

        params.forEach((value, key) => {
          this.setInputValue(key, value);
        });
      }

      setInputValue(name, value) {
        const input = this.form.querySelector(`[name="${CSS.escape(name)}"]`);
        if (!input) return;

        if (input.tagName === "SELECT") {
          input.value = value;
        } else if (input.type === "checkbox" || input.type === "radio") {
          if (input.value === value) input.checked = true;
        } else {
          input.value = value;
        }
      }

      updateResultsCount(count) {
        if (this.resultsCount) {
          this.resultsCount.textContent = `${count} products`;
        }
      }

      handleFilterChange(event) {
        this.clearPendingUpdate();

        this.pendingUpdate = setTimeout(() => {
          this.updateAvailability();
        }, this.DEBOUNCE_DELAY);
      }

      clearPendingUpdate() {
        if (this.pendingUpdate) {
          clearTimeout(this.pendingUpdate);
          this.pendingUpdate = null;
        }
      }

      showLoadingState() {
        if (this.loadingOverlay) {
          this.loadingOverlay.classList.add("is-visible");
        }
      }

      hideLoadingState() {
        if (this.loadingOverlay) {
          this.loadingOverlay.classList.remove("is-visible");
        }
      }

      async updateAvailability() {
        if (this.isUpdating || !this.form) return;

        this.isUpdating = true;
        this.showLoadingState();

        try {
          const formData = new FormData(this.form);
          const params = this.buildSearchParams(formData);
          const cacheKey = params.toString();

          if (this.availabilityCache.has(cacheKey)) {
            this.applyAvailabilityUpdates(this.availabilityCache.get(cacheKey));
            return;
          }

          const availabilityData = await this.fetchAvailabilityData(params);

          if (availabilityData) {
            this.cacheAvailabilityData(cacheKey, availabilityData);
            this.applyAvailabilityUpdates(availabilityData);
          }
        } catch (error) {
          console.error("Error updating filter availability:", error);
        } finally {
          this.isUpdating = false;
          this.hideLoadingState();
        }
      }

      async fetchAvailabilityData(params) {
        const url = new URL(window.location);
        const fetchUrl = `${url.pathname}?${params.toString()}&section_id=${this.getSectionId()}`;

        const response = await fetch(fetchUrl);

        if (!response.ok) {
          throw new Error("Failed to fetch filter updates");
        }

        const html = await response.text();
        return this.parseAvailabilityFromHtml(html);
      }

      parseAvailabilityFromHtml(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newFacets = doc.querySelector("custom-facets");

        return newFacets ? this.extractAvailabilityData(newFacets) : null;
      }

      cacheAvailabilityData(key, data) {
        this.availabilityCache.set(key, data);

        if (this.availabilityCache.size > this.MAX_CACHE_SIZE) {
          const firstKey = this.availabilityCache.keys().next().value;
          this.availabilityCache.delete(firstKey);
        }
      }

      extractAvailabilityData(facetsElement) {
        return {
          inputs: this.extractInputsData(facetsElement),
          resultsCount: this.extractResultsCount(facetsElement),
        };
      }

      extractInputsData(facetsElement) {
        const inputs = facetsElement.querySelectorAll(CustomFacets.SELECTORS.FILTER_INPUTS);
        const inputsData = [];

        inputs.forEach((input) => {
          const option = input.closest(CustomFacets.SELECTORS.FILTER_OPTIONS);
          const countElement = option?.querySelector(CustomFacets.SELECTORS.COUNT_ELEMENTS);

          inputsData.push({
            name: input.name,
            value: input.value,
            disabled: input.disabled,
            count: this.parseCount(countElement?.textContent),
          });
        });

        return inputsData;
      }

      extractResultsCount(facetsElement) {
        const resultsElement = facetsElement.querySelector("[data-facets-results]");
        return this.parseCount(resultsElement?.textContent);
      }

      parseCount(text) {
        if (!text) return 0;
        const match = text.match(/\d+/);
        return match ? parseInt(match[0]) : 0;
      }

      applyAvailabilityUpdates(data) {
        if (!data || !this.form) return;

        const updates = this.prepareAvailabilityUpdates(data);

        requestAnimationFrame(() => {
          updates.forEach((update) => update());

          if (data.resultsCount !== undefined) {
            this.updateResultsCount(data.resultsCount);
          }
        });
      }

      prepareAvailabilityUpdates(data) {
        const updates = [];

        data.inputs.forEach((inputData) => {
          const input = this.findInput(inputData.name, inputData.value);
          if (!input) return;

          const option = input.closest(CustomFacets.SELECTORS.FILTER_OPTIONS);
          const countElement = option?.querySelector(CustomFacets.SELECTORS.COUNT_ELEMENTS);

          if (input.disabled !== inputData.disabled) {
            updates.push(() => {
              input.disabled = inputData.disabled;
            });
          }

          if (countElement && inputData.count !== undefined) {
            updates.push(() => {
              countElement.textContent = `(${inputData.count})`;
            });
          }
        });

        return updates;
      }

      findInput(name, value) {
        return this.form.querySelector(`input[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
      }
    }

    customElements.define("custom-facets", CustomFacets);
  }
})();
